/**
 * Change Intelligence
 * 
 * Diff view grouping changes into meaningful buckets with impact analysis.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { usePostHog } from '@posthog/react';
import { DarkPanel } from '@/components/ui/DarkPanel';
import { Badge } from '@/components/ui/badge';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { API_BASE_URL } from '@/config';
import MainLayout from '@/components/layout/MainLayout';
import { EmptySnapshotState } from '@/components/project-state/EmptySnapshotState';
import { PanelLoading } from '@/components/ui/loading';

interface DiffData {
  summary: any;
  activities_added: any[];
  activities_modified: any[];
  milestone_changes: any[];
  float_changes: any[];
  critical_path_changes: any[];
}

export const ChangeIntelligence: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const { theme } = useAppTheme();
  const posthog = usePostHog();
  const [diffData, setDiffData] = useState<DiffData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (projectId && posthog) {
      posthog.capture('schedule_compare_viewed', { project_id: projectId });
    }
  }, [projectId, posthog]);

  useEffect(() => {
    if (projectId) {
      fetchDiff();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchDiff recreated each render; projectId and searchParams in deps
  }, [projectId, searchParams]);

  const fetchDiff = async () => {
    try {
      setLoading(true);
      setError(null);
      const fromId = searchParams.get('from');
      const url = fromId
        ? `${API_BASE_URL}/v1/diffs/${projectId}?from_snapshot_id=${fromId}`
        : `${API_BASE_URL}/v1/diffs/${projectId}/latest`;
      
      const response = await fetch(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch diff');
      }
      const data = await response.json();
      
      // Check if this is a message response (not an error, just informational)
      if (data.message && data.requires_baseline) {
        setDiffData(null);
        setError(data.message);
      } else {
        setDiffData(data);
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load changes');
      setDiffData(null);
    } finally {
      setLoading(false);
    }
  };

  const { summary } = diffData || {};

  return (
    <MainLayout title="Change Intelligence">
      {loading ? (
        <PanelLoading message="Loading changes..." className="p-8" />
      ) : error || !diffData ? (
        <EmptySnapshotState
          title="No Changes Available"
          message={error || "Upload a XER file to create snapshots and view change intelligence. Set a baseline snapshot to track changes from a reference point."}
        />
      ) : (
        <div className="p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <DarkPanel className="p-4">
              <div className="text-sm text-gray-500 mb-1">Activities Added</div>
              <div className="text-2xl font-bold">{summary.activities_added || 0}</div>
            </DarkPanel>
            <DarkPanel className="p-4">
              <div className="text-sm text-gray-500 mb-1">Milestone Changes</div>
              <div className="text-2xl font-bold">{summary.milestone_changes || 0}</div>
            </DarkPanel>
            <DarkPanel className="p-4">
              <div className="text-sm text-gray-500 mb-1">Float Changes</div>
              <div className="text-2xl font-bold">{summary.float_changes || 0}</div>
            </DarkPanel>
            <DarkPanel className="p-4">
              <div className="text-sm text-gray-500 mb-1">Critical Path</div>
              <div className="text-2xl font-bold">{summary.critical_path_changes || 0}</div>
            </DarkPanel>
          </div>

          {/* Milestone Changes */}
          {diffData.milestone_changes && diffData.milestone_changes.length > 0 && (
            <DarkPanel className="p-6">
              <h2 className="text-lg font-semibold mb-4">Milestone Changes</h2>
              <div className="space-y-3">
                {diffData.milestone_changes.slice(0, 10).map((change, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded">
                    <div>
                      <div className="font-medium">{change.milestone_name}</div>
                      <div className="text-sm text-gray-500">{change.date_field}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {change.delta_days && change.delta_days > 0 ? (
                        <TrendingUp className="h-4 w-4 text-red-500" />
                      ) : change.delta_days && change.delta_days < 0 ? (
                        <TrendingDown className="h-4 w-4 text-green-500" />
                      ) : (
                        <Minus className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm">
                        {change.delta_days ? `${change.delta_days > 0 ? '+' : ''}${change.delta_days.toFixed(1)} days` : 'No change'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </DarkPanel>
          )}

          {/* Float Changes */}
          {diffData.float_changes && diffData.float_changes.length > 0 && (
            <DarkPanel className="p-6">
              <h2 className="text-lg font-semibold mb-4">Float Changes</h2>
              <div className="space-y-3">
                {diffData.float_changes
                  .filter((fc: any) => fc.crossed_threshold)
                  .slice(0, 10)
                  .map((change: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded">
                      <div>
                        <div className="font-medium">Activity {change.activity_id}</div>
                        <div className="text-sm text-gray-500">
                          {change.old_float?.toFixed(1)} → {change.new_float?.toFixed(1)} hours
                        </div>
                      </div>
                      {change.new_float < 0 && (
                        <Badge variant="destructive">Negative Float</Badge>
                      )}
                    </div>
                  ))}
              </div>
            </DarkPanel>
          )}
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ChangeIntelligence;

